import { appVer } from '@app/cnst'
import { buildInfo } from '@app/util/buildInfo.util'
import { _filterFalsyValues, _stringify, Primitive, StringMap } from '@naturalcycles/js-lib'
import { ExternalAccountId } from '@naturalcycles/shared'
import {
  addBreadcrumb,
  Breadcrumb,
  BrowserOptions,
  captureException,
  captureMessage,
  init as sentryBrowserInit,
  rewriteFramesIntegration,
  setExtras,
  setTags,
  setUser,
  SeverityLevel,
} from '@sentry/browser'
import { type CaptureContext, normalize } from '@sentry/core'
import { botDetectionService, isE2e } from '@src/environments/env.util.js'
import { env } from '@src/environments/environment.js'

// Important!
// Please try to avoid importing many services here (e.g StoreService),
// since it becomes vulnerable to circular dependencies

const { sentryDsn: dsn, name: environment, prod } = env

class SentryService {
  constructor() {
    if (env.test) return // no need to init Sentry in test
    Object.assign(globalThis, { sentryService: this }) // debug

    const opt: BrowserOptions = {
      dsn,
      environment,
      release: buildInfo.ver,
      maxValueLength: 2000, // default is 250 characters
      maxBreadcrumbs: 50, // to prevent "413 Request Entity Too Large" error
      beforeSend: (event, _hint) => {
        // todo: use sentryGroupingStrategy from js-lib as soon as it's ready
        // event.fingerprint = ['{{ default }}', _stringify(hint?.originalException)]

        return event
      },
      // todo: figure out how to read RemoteConfig.sentrySampleRate from here
      sampleRate: env.prod ? 0.2 : 1,
      integrations: [rewriteFramesIntegration()], // Allows to apply a transformation to each frame of the stack trace. In such a way sourcemaps can be mapped with the source code in IOS app.
    }

    sentryBrowserInit(opt)

    this.setTags({
      appVer,
      buildInfo_ver: buildInfo.ver, // eslint-disable-line @typescript-eslint/naming-convention
      buildInfo_env: buildInfo.env, // eslint-disable-line @typescript-eslint/naming-convention
      botReason: botDetectionService.getBotReason(),
      cdp: botDetectionService.isCDP(),
      e2e: isE2e,
    })
  }

  // Returns lastEventId
  captureException(err: any, captureContext?: CaptureContext): string | undefined {
    if (err?.cause?.data?.userFriendly) return // ignore userFriendly errors

    const errorString = _stringify(err, { includeErrorData: true })

    if (prod) {
      sentryService.addBreadcrumb({
        message: errorString,
      })
    } else {
      console.error(errorString)
    }

    return captureException(err, captureContext)
  }

  // Returns lastEventId
  captureMessage(message: string, severity?: SeverityLevel): string {
    return captureMessage(message, severity)
  }

  addBreadcrumb(crumb: Breadcrumb): void {
    addBreadcrumb({
      ...crumb,
      data: crumb.data ? normalize(crumb.data) : undefined,
    })
  }

  setExtras(ctx: any): void {
    setExtras(ctx)
  }

  setUser(id: ExternalAccountId): void {
    setUser({ id })
  }

  setTags(tags: StringMap<Primitive>): void {
    setTags(_filterFalsyValues(tags))
  }
}

export const sentryService = new SentryService()
